import { useCallback, useEffect, useRef, useState } from 'react';
import type { BlockNoteEditor } from '@blocknote/core';

import type { BracketMatch } from '../components/BlockNoteBracketHighlightPlugin';
import { bracketHighlightPluginKey } from '../components/BlockNoteBracketHighlightPlugin';

export interface BracketIndicatorPosition {
  top: number;
  height: number;
  element: HTMLElement;
  match: BracketMatch;
}

/**
 * Hook to track positions of bracket highlight elements in BlockNote editor
 * Returns array of positions relative to editor container
 */
export function useBracketHighlightIndicators(
  editor: BlockNoteEditor | undefined,
  editorContainerRef: React.RefObject<HTMLElement | null>,
  enabled: boolean = true,
): BracketIndicatorPosition[] {
  const [positions, setPositions] = useState<BracketIndicatorPosition[]>([]);
  const updateTimeoutRef = useRef<NodeJS.Timeout | undefined>(undefined);
  const mutationObserverRef = useRef<MutationObserver | null>(null);
  const resizeObserverRef = useRef<ResizeObserver | null>(null);
  const scrollListenerRef = useRef<((this: HTMLElement, ev: Event) => void) | null>(null);
  const unsubscribeRef = useRef<(() => void) | null>(null);

  // Calculate positions of bracket highlight elements
  const calculatePositions = useCallback(() => {
    if (!editor || !editorContainerRef.current || !enabled) {
      setPositions([]);
      return;
    }

    try {
      // Access the underlying TipTap/ProseMirror instance
      /* eslint-disable @typescript-eslint/no-explicit-any, @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-member-access */
      const tiptapEditor = (editor as any)._tiptapEditor;
      if (!tiptapEditor || !tiptapEditor.view || !tiptapEditor.view.dom) {
        setPositions([]);
        return;
      }

      const editorDOM = tiptapEditor.view.dom as HTMLElement;
      const container = editorContainerRef.current;
      const view = tiptapEditor.view;

      // Get the bracket highlight plugin state to extract match data
      const decorations = bracketHighlightPluginKey.getState(view.state);
      if (!decorations) {
        setPositions([]);
        return;
      }

      // Find all bracket highlight elements within the editor DOM
      const bracketElements = editorDOM.querySelectorAll('.bracket-highlight');

      if (bracketElements.length === 0) {
        setPositions([]);
        return;
      }

      // Get container's bounding rect for relative positioning
      const containerRect = container.getBoundingClientRect();

      // Calculate positions relative to container (the editor container box)
      const newPositions: BracketIndicatorPosition[] = [];

      // Get all bracket matches from the document using the same logic as the plugin
      // This ensures we have the correct from/to positions
      const doc = view.state.doc;
      const regex = /\[[^\]]+\]/g;
      const allMatches: BracketMatch[] = [];

      doc.descendants((node: any, pos: number) => {
        if (node.isText && node.text) {
          let match;
          const text = node.text;
          regex.lastIndex = 0;

          while ((match = regex.exec(text)) !== null) {
            const from = pos + match.index;
            const to = from + match[0].length;
            allMatches.push({ from, to, text: match[0] });
          }
        }
      });

      bracketElements.forEach(element => {
        const elementRect = element.getBoundingClientRect();
        // Calculate top position relative to container
        // elementRect.top is relative to viewport
        // containerRect.top is relative to viewport
        // We want position relative to container's top edge
        const elementTopRelativeToContainer = elementRect.top - containerRect.top;
        // Account for container's scroll offset if it's scrollable
        const top = elementTopRelativeToContainer + (container.scrollTop || 0);

        // Get the height of the bracket element
        const height = elementRect.height;

        // Extract BracketMatch from the decoration
        // Get the bracket text from the data attribute
        const bracketText = element.getAttribute('data-bracket-text');
        if (!bracketText) {
          return; // Skip if no bracket text found
        }

        // Find the corresponding match by text
        // Since there could be multiple brackets with the same text, we need to match
        // by finding decorations at potential positions
        let match: BracketMatch | null = null;

        // Find matches that have the same text
        const matchingMatches = allMatches.filter(m => m.text === bracketText);

        // For each potential match, check if there's a decoration at that position
        for (const potentialMatch of matchingMatches) {
          const found = decorations.find(potentialMatch.from, potentialMatch.to);
          if (found.length > 0) {
            // Verify the text matches
            const docText = doc.textBetween(potentialMatch.from, potentialMatch.to);
            if (docText === bracketText) {
              match = potentialMatch;
              break;
            }
          }
        }

        // If we still don't have a match, use the first one with matching text
        // This is a fallback for edge cases
        if (!match && matchingMatches.length > 0) {
          match = matchingMatches[0];
        }

        // Only add if we found a valid match
        if (match) {
          newPositions.push({ top, height, element: element as HTMLElement, match });
        }
      });

      // Sort by top position
      newPositions.sort((a, b) => a.top - b.top);

      setPositions(newPositions);
      /* eslint-enable @typescript-eslint/no-explicit-any, @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-member-access */
    } catch (error) {
      setPositions([]);
    }
  }, [editor, editorContainerRef, enabled]);

  // Debounced update function
  const debouncedUpdate = useCallback(() => {
    if (updateTimeoutRef.current) {
      clearTimeout(updateTimeoutRef.current);
    }
    updateTimeoutRef.current = setTimeout(() => {
      calculatePositions();
    }, 100);
  }, [calculatePositions]);

  // Set up observers and listeners
  useEffect(() => {
    if (!editor || !editorContainerRef.current || !enabled) {
      setPositions([]);
      return;
    }

    try {
      /* eslint-disable @typescript-eslint/no-explicit-any, @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-member-access */
      const tiptapEditor = (editor as any)._tiptapEditor;
      if (!tiptapEditor || !tiptapEditor.view || !tiptapEditor.view.dom) {
        return;
      }

      const editorDOM = tiptapEditor.view.dom as HTMLElement;
      const container = editorContainerRef.current;

      // Initial calculation
      calculatePositions();

      // Cleanup any existing observers before creating new ones
      if (mutationObserverRef.current) {
        mutationObserverRef.current.disconnect();
        mutationObserverRef.current = null;
      }
      if (resizeObserverRef.current) {
        resizeObserverRef.current.disconnect();
        resizeObserverRef.current = null;
      }
      if (scrollListenerRef.current) {
        container.removeEventListener('scroll', scrollListenerRef.current);
        scrollListenerRef.current = null;
      }
      if (unsubscribeRef.current) {
        unsubscribeRef.current();
        unsubscribeRef.current = null;
      }

      // MutationObserver for content changes
      const mutationObserver = new MutationObserver(() => {
        debouncedUpdate();
      });
      mutationObserverRef.current = mutationObserver;

      mutationObserver.observe(editorDOM, {
        childList: true,
        subtree: true,
        attributes: true,
        attributeFilter: ['class', 'style'],
      });

      // ResizeObserver for container resize
      const resizeObserver = new ResizeObserver(() => {
        debouncedUpdate();
      });
      resizeObserverRef.current = resizeObserver;

      resizeObserver.observe(container);

      // Scroll listener
      const handleScroll = () => {
        debouncedUpdate();
      };
      scrollListenerRef.current = handleScroll;
      container.addEventListener('scroll', handleScroll, { passive: true });

      // Editor onChange callback as additional trigger
      const unsubscribe = editor.onChange(() => {
        debouncedUpdate();
      });
      unsubscribeRef.current = unsubscribe || null;

      return () => {
        // Cleanup all observers and listeners
        if (mutationObserverRef.current) {
          mutationObserverRef.current.disconnect();
          mutationObserverRef.current = null;
        }
        if (resizeObserverRef.current) {
          resizeObserverRef.current.disconnect();
          resizeObserverRef.current = null;
        }
        if (scrollListenerRef.current && editorContainerRef.current) {
          editorContainerRef.current.removeEventListener('scroll', scrollListenerRef.current);
          scrollListenerRef.current = null;
        }
        if (unsubscribeRef.current) {
          unsubscribeRef.current();
          unsubscribeRef.current = null;
        }
        if (updateTimeoutRef.current) {
          clearTimeout(updateTimeoutRef.current);
          updateTimeoutRef.current = undefined;
        }
      };
      /* eslint-enable @typescript-eslint/no-explicit-any, @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-member-access */
    } catch (error) {
      // Silently handle errors
    }
  }, [editor, editorContainerRef, enabled, calculatePositions, debouncedUpdate]);

  return positions;
}
